/* tslint:disable */
/* eslint-disable */
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { ClinicInvoiceCreateDto, ClinicInvoiceDto } from '../models/clinic-invoice-create-dto';
import { InvoiceStatus } from '../models/invoice-status';
import { UserDto } from '../models/user-dto';
import { AccountService } from './account.service';

export interface BaseResponse<T> {
  succeeded: boolean;
  message?: string;
  data?: T;
}

export interface AddCreditsRequest {
  amount: number;
  notes?: string;
  expiryDate?: string;
}

export interface PatientCreditsResponse {
  patientId: number;
  availableCredits: number;
  formattedAmount: string;
}

@Injectable({
  providedIn: 'root',
})
export class BillingService extends BaseService {
  constructor(
    config: ApiConfiguration,
    http: HttpClient,
    private accountService: AccountService
  ) {
    super(config, http);
  }

  /**
   * Gets all practitioners (users) for the practitioner dropdown
   */
  getPractitioners(): Observable<UserDto[]> {
    return this.accountService.apiAccountSearchAllGet$Json({
      searchTerm: '',
      archived: false
    }).pipe(
      map(response => response.data || [])
    );
  }

  /**
   * Creates a new clinic invoice
   */
  createInvoice(dto: ClinicInvoiceCreateDto): Observable<ClinicInvoiceDto> {
    return this.http.post<ClinicInvoiceDto>(`${this.rootUrl}/api/billing/invoices`, dto);
  }

  /**
   * Gets an invoice by ID
   */
  getInvoice(invoiceId: number): Observable<ClinicInvoiceDto> {
    return this.http.get<ClinicInvoiceDto>(`${this.rootUrl}/api/billing/invoices/${invoiceId}`);
  }

  /**
   * Gets all clinic invoices
   */
  getAllInvoices(): Observable<BaseResponse<ClinicInvoiceDto[]>> {
    return this.http.get<BaseResponse<ClinicInvoiceDto[]>>(`${this.rootUrl}/api/clinic/invoices`);
  }

  /**
   * Gets all invoices for a patient
   */
  getPatientInvoices(patientId: number): Observable<ClinicInvoiceDto[]> {
    return this.http.get<ClinicInvoiceDto[]>(`${this.rootUrl}/api/billing/patients/${patientId}/invoices`);
  }

  /**
   * Updates an invoice (e.g., mark as paid, change status)
   */
  updateInvoice(invoiceId: number, updateData: any): Observable<ClinicInvoiceDto> {
    return this.http.put<ClinicInvoiceDto>(`${this.rootUrl}/api/billing/invoices/${invoiceId}`, updateData);
  }

  /**
   * Gets available credits for a patient
   */
  getAvailableCredits(patientId: number): Observable<PatientCreditsResponse> {
    return this.http.get<PatientCreditsResponse>(`${this.rootUrl}/api/billing/patients/${patientId}/credits`);
  }

  /**
   * Adds credits to a patient account
   */
  addCreditsToPatient(patientId: number, request: AddCreditsRequest): Observable<any> {
    return this.http.post(`${this.rootUrl}/api/billing/patients/${patientId}/credits`, request);
  }

  /**
   * Applies available credits to an invoice
   */
  applyCreditsToInvoice(invoiceId: number): Observable<{ success: boolean; message: string }> {
    return this.http.post<{ success: boolean; message: string }>(`${this.rootUrl}/api/billing/invoices/${invoiceId}/apply-credits`, {});
  }

  /**
   * Calculates invoice totals including taxes
   */
  calculateInvoiceTotals(invoiceId: number): Observable<ClinicInvoiceDto> {
    return this.http.post<ClinicInvoiceDto>(`${this.rootUrl}/api/billing/invoices/${invoiceId}/calculate`, {});
  }

  /**
   * Cancels an invoice
   */
  cancelInvoice(invoiceId: number, reason: string): Observable<{ success: boolean; message: string }> {
    return this.http.post<{ success: boolean; message: string }>(`${this.rootUrl}/api/billing/invoices/${invoiceId}/cancel`, { reason });
  }
} 